// Yjs cache bridge.
//
// In Electron, this lives at <userData>/yjs/<sha-of-filepath>.bin and
// is a CRDT update blob. The renderer calls yjsLoad/yjsSave so its
// LocalAdapter can hydrate the editor before the file body is parsed.
//
// On web, the renderer's `doc-store.openDraft` already uses
// y-indexeddb under the same identity (the Lastslate-ID UUID), so
// these IPC entry points are effectively pass-throughs that store
// blobs in a small auxiliary IndexedDB store. They're rarely the
// authoritative copy on web — doc-store wins — but the renderer
// guards on `yjsLoad` returning a non-null `bytes` so we keep the
// contract honest.

const DB_NAME = 'lastslate-yjs-cache';
const STORE = 'kv';

let _dbPromise = null;
function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}
async function idbPut(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
async function idbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function keyForPath(filePath, draftId) {
  const base = String(filePath || '');
  return draftId ? `path:${base}|draft:${draftId}` : `path:${base}`;
}
function keyForCloud(cloudId, draftId) {
  return `cloud:${cloudId}|draft:${draftId}`;
}

export function buildYjsCacheHandlers() {
  return {
    async yjsLoad(arg) {
      const filePath = typeof arg === 'string' ? arg : arg && arg.filePath;
      const draftId = typeof arg === 'object' && arg ? arg.draftId : null;
      if (!filePath) return { ok: true, bytes: null };
      try {
        const buf = await idbGet(keyForPath(filePath, draftId));
        return { ok: true, bytes: buf || null };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
    async yjsSave(filePathOrArgs, maybeBytes, opts) {
      let filePath, bytes, draftId;
      if (typeof filePathOrArgs === 'string') {
        filePath = filePathOrArgs;
        bytes = maybeBytes;
        draftId = (opts && opts.draftId) || null;
      } else {
        const a = filePathOrArgs || {};
        filePath = a.filePath;
        bytes = a.bytes;
        draftId = a.draftId || null;
      }
      if (!filePath || !bytes || !bytes.length) {
        return { ok: false, error: 'filePath + non-empty bytes required' };
      }
      try {
        await idbPut(keyForPath(filePath, draftId), bytes);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
    async yjsSaveCloud(args) {
      const { cloudId, draftId, bytes } = args || {};
      if (!cloudId || !draftId) return { ok: false, error: 'cloudId+draftId required' };
      if (!bytes || !bytes.length) return { ok: false, error: 'non-empty bytes required' };
      try {
        await idbPut(keyForCloud(cloudId, draftId), bytes);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
    async yjsLoadCloud(args) {
      const { cloudId, draftId } = args || {};
      if (!cloudId || !draftId) return { ok: false, error: 'cloudId+draftId required' };
      try {
        const buf = await idbGet(keyForCloud(cloudId, draftId));
        return { ok: true, bytes: buf || null };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
    async yjsClearCloud(args) {
      const { cloudId, draftId } = args || {};
      if (!cloudId || !draftId) return { ok: false, error: 'cloudId+draftId required' };
      try {
        await idbDelete(keyForCloud(cloudId, draftId));
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      }
    },
  };
}
